import type { Translations } from './types.js'

export const fr: Translations = {
  common: {
    loading: 'Chargement...',
    error: 'Erreur',
    previous: 'Précédent',
    next: 'Suivant',
    save: 'Sauvegarder',
    saving: 'Sauvegarde...',
    saved: 'Sauvegarde !',
    cancel: 'Annuler',
    delete: 'Supprimer',
    deleting: 'Suppression...',
    download: 'Télécharger',
    view: 'Voir',
    viewAll: 'Voir tout',
    page: 'Page',
    noResults: 'Aucun résultat pour votre recherche',
    resetFilters: 'Réinitialiser les filtres',
    search: 'Rechercher',
    all: 'Tous',
    retry: 'Réessayer',
    create: 'Créer',
    creating: 'Création...',
    backToHome: "Retour à l'accueil",
    pageNotFound: 'Page non trouvée',
    pageNotFoundDesc: "La page que vous cherchez n'existe pas ou a été déplacée.",
    allStatuses: 'Tous les statuts',
    close: 'Fermer',
    notes: 'Notes',
    notesPlaceholder: 'Notes optionnelles',
    errorLoading: 'Une erreur est survenue lors du chargement des données.',
    errorUnexpected: 'Une erreur est survenue',
    errorUnexpectedDesc: "L'application a rencontré un problème inattendu. Vous pouvez essayer de recharger la page.",
  },
  login: {
    title: 'Connexion',
    subtitle: 'Accédez à votre espace WasteSite',
    emailLabel: 'Adresse email',
    emailPlaceholder: 'votre@email.com',
    submit: 'Envoyer le lien magique',
    sending: 'Envoi en cours...',
    successTitle: 'Email envoyé !',
    successMessage: 'Vérifiez votre boîte mail et cliquez sur le lien pour vous connecter.',
    errorMessage: 'Une erreur est survenue. Veuillez réessayer.',
    sessionReplaced: 'Un autre appareil utilise ce compte',
    sessionReplacedHint: 'Reconnectez-vous ou ajoutez un utilisateur à votre équipe.',
    backToHome: 'Retour à l\'accueil',
    ssoButton: 'Connexion SSO (Atelya)',
  },
  nav: {
    principal: 'Principal',
    data: 'Données',
    account: 'Compte',
    dashboard: 'Tableau de bord',
    sites: 'Chantiers',
    wasteEntry: 'Saisie déchet',
    reports: 'Rapports',
    bordereaux: 'Bordereaux',
    compliance: 'Conformité',
    collectors: 'Collecteurs',
    ewcCodes: 'Codes EWC',
    billing: 'Abonnement',
    settings: 'Paramètres',
    faq: 'FAQ',
    team: 'Équipe',
    ceoDashboard: 'Vue CEO',
    bsdInbox: 'Inbox BSD',
    newWaste: 'Nouveau déchet',
    belgium: 'Belgique',
    france: 'France',
    logout: 'Déconnexion',
    driverDashboard: 'Ma tournée',
    diwassWidget: 'DIWASS',
  },
  dashboard: {
    title: 'Tableau de bord',
    subtitle: "Vue d'ensemble",
    activeSites: 'Chantiers actifs',
    newThisMonth: '+{count} ce mois',
    valorizationRate: 'Taux valorisation global',
    vsTarget: '{value}% vs objectif EU 70%',
    wasteThisMonth: 'Déchets ce mois',
    vsLastMonth: '{value}% vs mois prec.',
    compliance: 'Conformité',
    incomplete: '{count} incomplet{plural}',
    alertBannerTitle: "En 2026, ne pas tracer vos d\u00e9chets de chantier n'est plus une option. C'est une amende.",
    alertBannerDesc: "Jusqu'\u00e0 150.000 EUR d'amende en France \u00b7 Obligation registre depuis Directive 2008/98/CE \u00b7 Objectif EU : 70% de valorisation C&D",
    checkCompliance: 'Vérifier ma conformité',
    costManagement: 'Coût gestion déchets (Mars)',
    costMonth: '{tonnes}t \u00b7 ~{avg}/t moy.',
    avgPerTonne: '/t moy.',
    recyclingCost: 'Recyclage',
    disposalCost: 'Élimination',
    asbestosCost: 'Amiante',
    economyTitle: 'Économie valorisation',
    savedVsLandfill: 'économisé vs 100% décharge',
    recycledAt: '{rate}% recyclé à {recycleCost}/t vs {disposalCost}/t décharge',
    wasteByType: 'Déchets par type',
    months12: '12 mois',
    valorizationTrend: 'Taux de valorisation',
    months6: '6 mois',
    euTarget: 'Objectif EU 70%',
    valorizationLabel: 'Taux valorisation',
    activeSitesTitle: 'Chantiers actifs',
    alerts: 'Alertes',
    noAlerts: 'Aucune alerte',
    compliant: 'Conforme',
    nonCompliant: 'Incomplet',
    regionalBreakdown: 'Répartition par région',
    noData: 'Aucune donnée',
    diwassDeadline: 'DIWASS — échéance',
    diwassDaysLeft: 'J-{days} jours',
    recentBsd: 'Activité récente — BSD',
    noRecentBsd: 'Aucun BSD récemment créé',
    recentBsdSite: 'Chantier',
    recentBsdTonnage: 'Tonnage',
    terrainAlerts: 'Alertes terrain',
    noTerrainAlerts: 'Aucune alerte active',
    valorizationGauge: 'Taux de valorisation',
    bsdEnCours: 'BSD en cours',
    tonnesValor: 'Tonnes valorisées',
    greeting: {
      morning: 'Bonjour',
      afternoon: 'Bon après-midi',
      evening: 'Bonsoir',
    },
  },
  sites: {
    title: 'Chantiers',
    addSite: 'Ajouter un chantier',
    viewList: 'Liste',
    viewMap: 'Carte',
    siteName: 'Nom du chantier',
    address: 'Adresse',
    country: 'Pays',
    status: 'Statut',
    date: 'Date',
    noSites: 'Aucun chantier',
    noSitesDesc: 'Ajoutez votre premier chantier pour commencer à suivre vos déchets et générer vos bordereaux.',
    coordinates: 'Coordonnées GPS',
    noCoordinates: 'Aucun chantier avec coordonnées GPS.',
    statusActive: 'Actif',
    statusCompleted: 'Terminé',
    statusArchived: 'Archivé',
    bsdCount: 'BSD en cours',
    tonnageTotal: 'Tonnage total',
    valorizationRateLabel: 'Valorisation',
    countries: {
      BE: 'Belgique',
      FR: 'France',
      NL: 'Pays-Bas',
      DE: 'Allemagne',
      LU: 'Luxembourg',
    },
  },
  wasteEntry: {
    title: 'Déclarations de déchets',
    newEntry: 'Nouvelle déclaration',
    ewcCode: 'Code EWC',
    quantity: 'Quantité',
    unit: 'Unité',
    weight: 'Poids (kg)',
    destination: 'Destination',
    allDestinations: 'Toutes les destinations',
    noEntries: 'Aucune déclaration',
    noEntriesDesc: 'Créez votre première déclaration de déchets pour commencer le suivi de vos évacuations.',
    destinations: {
      recyclage: 'Recyclage',
      valorisation_energetique: 'Valorisation énergétique',
      elimination: 'Élimination',
      reutilisation: 'Réutilisation',
    },
    units: {
      kg: 'kg',
      t: 'tonnes',
      m3: 'm\u00b3',
      big_bag: 'Big bag',
      benne_8m3: 'Benne 8m\u00b3',
      benne_15m3: 'Benne 15m\u00b3',
      benne_30m3: 'Benne 30m\u00b3',
    },
    deleteConfirm: 'Confirmer la suppression',
    deleteWarning: 'Cette action est irréversible. Voulez-vous vraiment supprimer cette déclaration ?',
    notesLabel: 'Notes (optionnel)',
    notesPlaceholder: 'Remarques, observations...',
    ewcHelper: 'Format : XX XX XX (ex : 17 01 01)',
    charCount: '{count}/500 caractères',
  },
  bordereaux: {
    title: 'Bordereaux de suivi',
    bsdNumber: 'N\u00b0 BSD',
    type: 'Type',
    producer: 'Producteur',
    collector: 'Collecteur',
    status: 'Statut',
    date: 'Date',
    noBsd: 'Aucun bordereau',
    noBsdDesc: 'Les bordereaux de suivi des déchets (BSD) apparaîtront ici une fois vos premières déclarations créées.',
    statuses: {
      draft: 'Brouillon',
      signed_producer: 'Signé producteur',
      collected: 'Collecté',
      signed_collector: 'Signé collecteur',
      received: 'Reçu',
      treated: 'Traité',
      archived: 'Archivé',
    },
    created: 'Bordereau créé avec succès',
    deleted: 'Bordereau supprimé',
    sign: 'Signer',
    archive: 'Archiver',
    weights: 'Pesées',
    weightDeparture: 'Poids départ (kg)',
    weightArrival: 'Poids arrivée (kg)',
    record: 'Enregistrer',
    discrepancy: 'Écart',
    discrepancyWarning: 'Attention > 5%',
    photos: 'Photos',
    loadingLabel: 'Chargement',
    unloadingLabel: 'Déchargement',
    upload: 'Envoyer',
    lifecycle: 'Cycle de vie',
    waste: 'Déchets',
    signatures: 'Signatures',
    adrTitle: 'Transport ADR — Matières dangereuses',
    unNumber: 'Numéro ONU',
    packingGroup: "Groupe d'emballage",
    transportCategory: 'Catégorie de transport',
    hazardLabels: 'Étiquettes de danger',
    none: 'Aucune',
    roles: {
      producer: 'Producteur',
      collector: 'Collecteur',
      treatmentFacility: 'Centre de traitement',
    },
    labels: {
      company: 'Entreprise',
      siret: 'TVA/BCE',
      address: 'Adresse',
      contact: 'Contact',
      phone: 'Téléphone',
      plate: 'Plaque',
      driver: 'Chauffeur',
      agreementNumber: 'N\u00b0 agr\u00e9ment',
    },
    breadcrumb: "Fil d'Ariane",
    description: 'Description',
    newBsd: 'Nouveau bordereau',
    destSite: 'Site de destination',
    wasteType: 'Type de déchet',
    actions: 'Actions',
    timelineTitle: 'Cycle de vie du BSD',
    timelineWaiting: 'En attente',
    timelineSteps: {
      draft: 'Créé',
      signed_producer: 'Signé producteur',
      collected: 'Transporté',
      weighed_departure: 'Pesée départ',
      weighed_arrival: 'Pesée arrivée',
      signed_collector: 'Signé collecteur',
      received: 'Réceptionné',
      treated: 'Traité',
      diwass_submitted: 'DIWASS soumis',
    },
  },
  reports: {
    title: 'Rapports',
    generate: 'Générer un rapport',
    generating: 'Génération en cours...',
    reportType: 'Type de rapport',
    format: 'Format',
    period: 'Période',
    dateFrom: 'Du',
    dateTo: 'Au',
    site: 'Chantier',
    allSites: 'Tous les chantiers',
    download: 'Télécharger le PDF',
    noReports: 'Aucun rapport',
    noReportsDesc: 'Les rapports seront générés automatiquement à partir de vos déclarations et bordereaux.',
    generatedSuccess: 'Rapport PDF généré et téléchargé.',
    types: {
      registre_complet: 'Registre complet',
      bilan_valorisation: 'Bilan valorisation',
      certificat_conformite: 'Certificat conformité',
      registre_diwass: 'Registre DIWASS',
      registre_brudalex: 'Registre Brudalex',
      registre_lap3: 'Registre LAP3 (Pays-Bas)',
      registre_krwg: 'Registre KrWG (Allemagne)',
    },
    typeDesc: {
      registre_complet: 'Registre chronologique de toutes les déclarations de déchets avec totaux.',
      bilan_valorisation: 'Taux de valorisation, répartition par filière et évolution mensuelle.',
      certificat_conformite: 'Document officiel de conformité réglementaire (Directive 2008/98/CE).',
      registre_diwass: 'Format belge DIWASS pour la déclaration des déchets de construction (codes 17xx).',
      registre_brudalex: 'Registre Brudalex — Région Bruxelles-Capitale. Traçabilité renforcée, objectif 90%.',
      registre_lap3: 'Registre LAP3 — Pays-Bas. Objectif de valorisation 95%, tri 5 fractions (Afvalwijzer), déclaration LMA > 50t.',
      registre_krwg: 'Abfallregister KrWG/GewAbfV — Allemagne. Tri 7 fractions obligatoire, objectif de valorisation 70%, Begleitschein.',
    },
  },
  compliance: {
    title: 'Échéances réglementaires',
    regulation: 'Réglementation',
    country: 'Pays',
    region: 'Region',
    deadline: 'Échéance',
    severity: 'Sévérité',
    description: 'Description',
    noDeadlines: 'Aucune échéance',
    noDeadlinesDesc: 'Les échéances réglementaires (DIWASS, Brudalex, inspections, renouvellements) apparaîtront ici une fois vos chantiers et déclarations configurés.',
    severities: {
      info: 'Info',
      warning: 'Attention',
      critical: 'Critique',
    },
    regions: {
      wallonie: 'Wallonie',
      bruxelles: 'Bruxelles-Capitale',
      flandre: 'Flandre',
      luxembourg: 'Luxembourg',
      france: 'France',
      germany: 'Allemagne',
    },
    regional: {
      title: 'Conformité régionale',
      compliant: 'Conforme',
      nonCompliant: 'Non conforme',
      noRegion: 'Région non déterminée',
      brudalexTitle: 'Brudalex — Bruxelles-Capitale',
      diwassTitle: 'DIWASS — Wallonie',
      planGestionRequired: 'Plan de gestion des déchets requis (chantier > 500 m²)',
      inventaireAmianteRequired: 'Inventaire amiante requis (bâtiment avant 2001)',
      bsdRequired: 'BSD obligatoire pour tout déchet (pas de seuil)',
      valorisation90: 'Objectif valorisation 90% (Bruxelles)',
      enregistrementTransporteur: 'Transporteur enregistré Bruxelles Environnement',
      registreTempsReel: 'Registre en temps réel (pas de délai 48h)',
    },
  },
  billing: {
    title: 'Abonnement',
    subtitle: 'Gérez votre plan et votre facturation',
    currentPlan: 'Plan actuel',
    active: 'Actif',
    free: 'Gratuit',
    perMonth: '/mois',
    perYear: '/an',
    monthly: 'Mensuel',
    annual: 'Annuel',
    manageSubscription: "Gérer l'abonnement",
    usage: 'Utilisation',
    activeSites: 'Chantiers actifs',
    declarationsMonth: 'Déclarations ce mois',
    ocrScansMonth: 'Scans OCR ce mois',
    changePlan: 'Changer de plan',
    testMode: 'Mode test Stripe',
    testCard: 'Utilisez la carte',
    popular: 'Populaire',
    choosePlan: 'Choisir ce plan',
    currentPlanBtn: 'Plan actuel',
    freePlan: 'Plan gratuit',
    onRequest: 'Sur devis',
    contactUs: 'Nous contacter',
    redirecting: 'Redirection...',
    paymentSuccess: 'Paiement réussi ! Votre abonnement est actif.',
    paymentCanceled: 'Paiement annulé. Vous pouvez réessayer à tout moment.',
    invoiceHistory: 'Historique de facturation',
    invoiceDate: 'Date',
    invoiceNumber: 'Numéro',
    invoiceAmount: 'Montant',
    invoiceStatus: 'Statut',
    paid: 'Payé',
    noInvoices: 'Aucune facture pour le moment',
    noInvoicesDesc: 'Les factures apparaîtront ici après votre premier paiement',
    stripeNotConfigured: 'Stripe non configuré — plans indisponibles en dev.',
    devPlansNotice: 'Plans affichés à titre indicatif. Stripe non configuré dans cet environnement.',
    yourPlan: 'Votre abonnement',
    upgrade: 'Passer à {name}',
    downgrade: 'Revenir à {name}',
    annualSaving: '{price}/an — 2 mois offerts',
  },
  settings: {
    title: 'Paramètres',
    subtitle: 'Configurez votre espace WasteSite',
    company: 'Entreprise',
    companyName: 'Nom',
    vatNumber: 'Numéro TVA',
    address: 'Adresse',
    email: 'Email contact',
    phone: 'Téléphone',
    activeCountries: 'Pays actifs',
    activeCountriesDesc: 'Sélectionnez les pays où vous opérez pour adapter la réglementation',
    language: 'Langue',
    notifications: 'Notifications',
    emailNotif: 'Notifications email',
    emailNotifDesc: 'Recevez les alertes et rapports par email',
    pushNotif: 'Notifications push',
    pushNotifDesc: 'Notifications en temps réel dans le navigateur',
    weeklyReport: 'Rapport hebdomadaire',
    weeklyReportDesc: 'Résumé automatique chaque lundi matin',
    complianceAlerts: 'Alertes conformité',
    complianceAlertsDesc: 'Notification immédiate en cas de non-conformité',
    bsdTracking: 'Suivi bordereaux',
    bsdTrackingDesc: 'Notifie quand un BSD change de statut',
    dangerZone: 'Zone danger',
    deleteAccount: 'Supprimer le compte',
    deleteAccountDesc: 'Toutes les données seront définitivement supprimées',
    changeLogo: 'Changer le logo',
    removeLogo: 'Supprimer le logo',
    gdpr: {
      title: 'Vie privée & RGPD',
      subtitle: 'Gérez vos données personnelles et exercez vos droits RGPD',
      export: 'Exporter mes données',
      exporting: 'Export en cours...',
      exportSuccess: 'Export téléchargé avec succès',
      deleteRequest: 'Demander la suppression',
      deleteRequestDesc: 'Demandez la suppression de toutes vos données (Art. 17 RGPD). Cette action est irréversible et sera traitée sous 30 jours.',
      deleteRequestConfirm: 'Confirmer la demande de suppression ? Cette action est irréversible.',
      deleteRequestSuccess: 'Demande de suppression enregistrée. Vous recevrez une confirmation par email.',
      history: 'Historique des demandes',
      noRequests: 'Aucune demande RGPD',
    },
  },
  trackdechets: {
    title: 'TrackDechets',
    sync: 'Synchroniser avec TrackDechets',
    syncing: 'Synchronisation en cours...',
    synced: 'Synchronisé',
    syncError: 'Erreur de synchronisation',
    notConfigured: 'TrackDechets non configuré',
    status: {
      pending: 'En attente',
      synced: 'Synchronisé',
      error: 'Erreur',
    },
  },
  diwass: {
    title: 'DIWASS',
    subtitle: 'Digital Waste Shipment System — Système européen de traçabilité des déchets',
    submit: 'Soumettre à DIWASS',
    submitting: 'Soumission en cours...',
    submitted: 'Soumis à DIWASS',
    syncError: 'Erreur de soumission DIWASS',
    notConfigured: 'DIWASS non configuré',
    notConfiguredDesc: 'Configurez votre clé API DIWASS dans les variables d\'environnement (DIWASS_API_KEY)',
    configured: 'DIWASS configuré',
    configuredDesc: 'Connecté au système central DIWASS',
    deadline: 'Échéance obligatoire : 21 mai 2026',
    regulation: 'Règlement UE 2024/1157 — Transferts de déchets',
    implementingAct: 'Règlement d\'exécution (UE) 2025/1290',
    notificationNumber: 'Numéro de notification',
    cancel: 'Annuler la notification',
    cancelling: 'Annulation en cours...',
    cancelled: 'Notification annulée',
    update: 'Mettre à jour',
    updating: 'Mise à jour en cours...',
    updated: 'Notification mise à jour',
    refresh: 'Rafraîchir le statut',
    refreshing: 'Actualisation...',
    alreadySubmitted: 'Déjà soumis à DIWASS',
    consentExpires: 'Consentement expire le',
    status: {
      draft: 'Brouillon',
      submitted: 'Soumis',
      acknowledged: 'Accusé de réception',
      consent_pending: 'Consentement en attente',
      consented: 'Consenti',
      objected: 'Objection',
      in_transit: 'En transit',
      received: 'Reçu',
      completed: 'Terminé',
      cancelled: 'Annulé',
      error: 'Erreur',
    },
    documentType: {
      notification: 'Notification (Annexe IA)',
      annexVII: 'Annexe VII (liste verte)',
    },
    fields: {
      notifier: 'Notifiant',
      consignee: 'Destinataire',
      carrier: 'Transporteur',
      waste: 'Déchets',
      shipment: 'Expédition',
      operation: 'Opération de traitement',
    },
  },
  rentri: {
    title: 'RENTRI',
    subtitle: 'Registro Elettronico Nazionale Tracciabilita Rifiuti',
    sync: 'Synchroniser avec RENTRI',
    syncing: 'Synchronisation RENTRI en cours...',
    synced: 'Synchronisé avec RENTRI',
    syncError: 'Erreur de synchronisation RENTRI',
    notConfigured: 'RENTRI non configuré',
    notConfiguredDesc: 'Configurez vos credentials RENTRI dans les variables d\'environnement (RENTRI_API_KEY, RENTRI_OPERATOR_CODE)',
    submitFir: 'Soumettre le FIR',
    firNumber: 'Numéro FIR',
    registro: 'Registre chronologique',
    registroEntry: 'Nouvelle entrée registre',
    carico: 'Carico (entrée)',
    scarico: 'Scarico (sortie)',
    status: {
      bozza: 'Brouillon',
      vidimato: 'Vidimé',
      partenza: 'Départ',
      trasporto: 'En transit',
      ricevuto: 'Reçu',
      completato: 'Terminé',
      annullato: 'Annulé',
      pending: 'En attente',
      synced: 'Synchronisé',
      error: 'Erreur',
    },
  },
  faq: {
    title: 'Questions fréquentes',
    subtitle: 'Tout ce que vous devez savoir sur la gestion des déchets BTP avec WasteSite',
    searchPlaceholder: 'Rechercher dans la FAQ...',
    categories: {
      regulation: 'Réglementation',
      usage: 'Utilisation',
      subscription: 'Abonnement',
      security: 'Sécurité',
    },
    contactTitle: 'Vous ne trouvez pas votre réponse ?',
    contactDesc: 'Notre équipe vous répond sous 24h, support prioritaire pour les plans Pro et Enterprise.',
    contactBtn: 'Contacter le support',
    items: [
      { category: 'regulation', question: 'Quels sont les codes EWC et pourquoi sont-ils obligatoires ?', answer: "Les codes EWC (European Waste Catalogue) sont une classification européenne standardisée des déchets. Chaque déchet produit sur chantier doit être identifié par son code EWC à 6 chiffres (ex: 17 01 01 pour le béton). C'est une obligation légale pour la traçabilité, le bordereau de suivi et la déclaration aux autorités compétentes." },
      { category: 'regulation', question: "Qu'est-ce que le taux de valorisation de 70% imposé par l'UE ?", answer: "La directive-cadre européenne 2008/98/CE impose un objectif de valorisation de 70% en poids des déchets de construction et démolition non dangereux d'ici 2020 (et au-delà). Cela signifie que 70% de vos déchets BTP doivent être recyclés, réutilisés ou valorisés énergétiquement plutôt qu'enfouis. WasteSite calcule ce taux automatiquement par chantier et globalement." },
      { category: 'regulation', question: 'Suis-je obligé de tenir un registre de déchets ?', answer: "Oui. En Belgique (AGW du 5 mars 2008) et en France (article R541-43 du Code de l'environnement), tout producteur de déchets doit tenir un registre chronologique de la production, du traitement et de l'élimination de ses déchets. WasteSite remplace le registre papier par un suivi numérique conforme et exportable." },
      { category: 'regulation', question: "Qu'est-ce qu'un bordereau de suivi de déchets (BSD) ?", answer: "Le BSD est un document administratif obligatoire qui accompagne chaque transport de déchets dangereux (et dans certains cas, non dangereux). Il trace le déchet du producteur au centre de traitement. WasteSite génère automatiquement les BSD au format PDF, pré-remplis avec vos données chantier et collecteur." },
      { category: 'regulation', question: 'Quelles sont les sanctions en cas de non-conformité ?', answer: "Les sanctions varient selon le pays. En Belgique, les infractions environnementales peuvent entraîner des amendes de 100 à 1 000 000 EUR et/ou un emprisonnement. En France, le dépôt illégal est puni de 2 ans de prison et 75 000 EUR d'amende. WasteSite vous alerte avant les échéances et signale les non-conformités pour éviter toute sanction." },
      { category: 'usage', question: 'Comment ajouter un nouveau chantier ?', answer: 'Allez dans Chantiers > cliquez sur "+ Nouveau chantier". Renseignez le nom du chantier, l\'adresse, le type de travaux (construction, démolition, rénovation) et le pays. Le chantier apparaît immédiatement dans votre tableau de bord avec ses statistiques propres.' },
      { category: 'usage', question: 'Comment fonctionne le scan OCR des documents ?', answer: "Lors de la saisie d'un déchet ou d'un bordereau, cliquez sur l'icône appareil photo. Prenez en photo le bon de pesée, le BSD papier ou tout document. Notre IA (Gemini Flash) extrait automatiquement les informations (poids, code EWC, transporteur, date) et pré-remplit le formulaire. Vous n'avez plus qu'à valider." },
      { category: 'usage', question: 'Puis-je gérer des chantiers dans plusieurs pays ?', answer: "Oui, à partir du plan Pro. WasteSite supporte actuellement la Belgique, la France, les Pays-Bas, l'Allemagne et le Luxembourg. Chaque pays a ses propres réglementations et formulaires, et WasteSite s'adapte automatiquement au contexte réglementaire du chantier." },
      { category: 'usage', question: 'Comment générer un rapport de conformité ?', answer: 'Allez dans Rapports, sélectionnez la période et les chantiers souhaités, puis cliquez sur "Générer". Le rapport inclut : taux de valorisation par chantier, répartition par type de déchet, liste des BSD émis, alertes de non-conformité et comparaison avec les objectifs EU. Export PDF ou Excel disponible.' },
      { category: 'usage', question: 'Mes données sont-elles sauvegardées automatiquement ?', answer: "Oui. Chaque saisie est enregistrée en temps réel dans notre base de données sécurisée. Les données sont sauvegardées quotidiennement avec rétention de 90 jours. En plan Enterprise, la rétention est illimitée et un export complet est disponible à tout moment." },
      { category: 'subscription', question: 'Quelle est la différence entre les plans Starter, Contractor, Collector et Enterprise ?', answer: "Starter (gratuit) : 1 chantier, 5 déclarations/mois, calendrier conformité. Contractor (49 EUR/mois) : 10 chantiers, déclarations illimitées, 100 scans OCR, bordereaux PDF, rapports conformité multi-pays. Collector (149 EUR/mois) : chantiers illimités, OCR illimité, tournées, inbox cross-tenant. Enterprise (sur devis) : utilisateurs illimités, API, SSO, portail inspecteurs DIWASS, SLA 99.9%." },
      { category: 'subscription', question: 'Puis-je changer de plan à tout moment ?', answer: "Oui. L'upgrade est immédiat : vous accédez aux nouvelles fonctionnalités dès la validation du paiement. En cas de downgrade, il prend effet à la fin de votre période de facturation en cours. Aucun engagement, résiliation possible à tout moment." },
      { category: 'subscription', question: 'Comment fonctionne la facturation ?', answer: "La facturation est mensuelle, par carte bancaire via Stripe (paiement sécurisé). Vous recevez une facture PDF par email à chaque échéance, également consultable dans Abonnement > Historique de facturation. TVA applicable selon votre pays." },
      { category: 'subscription', question: 'Proposez-vous un tarif annuel ou pour les groupes ?', answer: "Oui. Contactez-nous pour un devis personnalisé : remise de 20% sur l'abonnement annuel, tarifs dégressifs pour les groupes de construction (3+ entités), et offres sur mesure pour les grands comptes avec plus de 50 chantiers actifs." },
      { category: 'security', question: 'Où sont stockées mes données ?', answer: "Vos données sont hébergées sur des serveurs européens (Belgique/France), conformes au RGPD. Le chiffrement est assuré en transit (TLS 1.3) et au repos (AES-256). Aucune donnée n'est partagée avec des tiers sans votre consentement explicite." },
      { category: 'security', question: 'WasteSite est-il conforme au RGPD ?', answer: "Oui, intégralement. Vous pouvez à tout moment exporter, rectifier ou supprimer vos données personnelles depuis Paramètres. Notre DPO est joignable à dpo@wastesite.eu. Le registre de traitement et l'analyse d'impact (DPIA) sont disponibles sur demande." },
      { category: 'security', question: "Qui a accès à mes données au sein de mon entreprise ?", answer: "Vous contrôlez les accès. L'administrateur du compte peut inviter des collaborateurs avec des rôles différents : Admin (accès total), Manager (chantiers assignés), Opérateur (saisie uniquement, pas de rapports ni paramètres). Chaque action est tracée dans le journal d'audit." },
    ],
  },
  ai: {
    chat: {
      title: 'Assistant IA Déchets',
      subtitle: 'Classification EWC, BSD, conformité',
      placeholder: 'Décrivez votre déchet ou posez une question...',
      thinking: 'Réflexion en cours...',
      welcomeTitle: 'Comment puis-je vous aider ?',
      welcomeDescription: 'Je peux classifier vos déchets, trouver les codes EWC, créer des déclarations et pré-remplir vos bordereaux.',
      quickClassify: 'Classifier un déchet de chantier',
      quickEwc: 'Trouver un code EWC',
      quickDangerous: 'Ce déchet est-il dangereux ?',
      poweredBy: 'Propulsé par Claude AI',
      errorGeneric: 'Une erreur est survenue. Veuillez réessayer.',
      close: 'Fermer le chat',
      send: 'Envoyer le message',
    },
    insights: {
      title: 'Insights IA',
      generatedAt: 'Généré à {time}',
      analyzing: 'Analyse en cours...',
      refresh: 'Actualiser',
      error: 'Impossible de générer les insights',
      retry: 'Réessayer',
      noInsights: 'Aucun insight disponible',
    },
    tools: {
      ewcSearch: 'Recherche EWC',
      wasteEntryCreate: 'Création déclaration',
      ocrExtractor: 'Extraction OCR',
      bsdPrefill: 'Pré-remplissage BSD',
    },
  },
  scanner: {
    scanBarcode: 'Scannez le code-barres du BSD ou du contenant',
    scanning: 'Scan en cours...',
    scanSuccess: 'Code scanné avec succès',
    scanError: 'Impossible de lire le code. Réessayez.',
    noCamera: 'Aucune caméra disponible sur cet appareil',
    permissionDenied: "Accès à la caméra refusé. Autorisez l'accès dans les paramètres.",
    pointCamera: 'Pointez la caméra vers le code-barres ou le QR code',
  },
  collectors: {
    title: 'Annuaire collecteurs',
    addCollector: 'Ajouter un collecteur',
    name: 'Nom du collecteur',
    contactPerson: 'Personne de contact',
    email: 'Email',
    phone: 'Téléphone',
    address: 'Adresse',
    country: 'Pays',
    wasteTypes: 'Types de déchets',
    certifications: 'Certifications',
    authorization: 'Agrément transport',
    status: 'Statut',
    active: 'Actif',
    inactive: 'Inactif',
    noCollectors: 'Aucun collecteur',
    noCollectorsDesc: 'Ajoutez vos collecteurs agréés pour les associer à vos déclarations et bordereaux.',
    search: 'Rechercher un collecteur...',
    filterCountry: 'Pays',
    filterWasteType: 'Type de déchet',
    filterStatus: 'Statut',
    allCountries: 'Tous les pays',
    allWasteTypes: 'Tous les types',
    allStatuses: 'Tous les statuts',
    certsCount: '{count} cert.',
    editCollector: 'Modifier',
    deleteCollector: 'Supprimer',
    deleteConfirm: 'Supprimer ce collecteur ? Cette action est irréversible.',
    formTitle: 'Nouveau collecteur',
    formTitleEdit: 'Modifier le collecteur',
    countries: {
      BE: 'Belgique',
      FR: 'France',
      NL: 'Pays-Bas',
      DE: 'Allemagne',
      LU: 'Luxembourg',
    },
    wasteTypeLabels: {
      dangerous: 'Dangereux',
      'non-dangerous': 'Non dangereux',
      inert: 'Inertes',
    },
    certName: 'Nom de la certification',
    certNumber: 'Numéro',
    certExpiry: 'Date expiration',
    addCertification: 'Ajouter une certification',
    removeCertification: 'Retirer',
    transportAuth: 'Numéro d\'agrément transport',
    contactInfo: 'Informations de contact',
    wasteInfo: 'Types de déchets acceptés',
    certInfo: 'Certifications et agrément',
    generalInfo: 'Informations générales',
    postalCode: 'Code postal',
    streetLabel: 'Rue',
    houseNumber: 'N°',
    addressNotFound: 'Adresse non trouvée',
    addressPlaceholder: 'ex: Rue de la Loi',
    postalCodePlaceholder: '1000',
    houseNumberPlaceholder: '16',
    errorAddressPostalCodeRequired: 'Code postal requis',
    errorAddressStreetRequired: 'Rue requise',
    errorAddressHouseNumberRequired: 'Numéro requis',
    addressVerified: 'Adresse vérifiée (registre officiel)',
  },
  pwa: {
    offlineMode: 'Mode hors-ligne — les données seront synchronisées',
    syncPending: 'en attente',
    syncing: 'Synchronisation en cours...',
    syncComplete: 'Données synchronisées',
    syncError: 'Erreur de synchronisation — réessayez ultérieurement',
    installApp: "Installer l'application",
    updateAvailable: 'Mise à jour disponible',
    updateDescription: 'Une nouvelle version de WasteSite est prête.',
    updateNow: 'Mettre à jour',
  },
  ceoDashboard: {
    title: 'Vue executive',
    subtitle: 'Indicateurs stratégiques et performance globale',
    revenue: 'Chiffre d\'affaires',
    revenueThisMonth: 'CA ce mois',
    revenueTrend: 'Évolution CA',
    activeSubscriptions: 'Abonnements actifs',
    wasteProcessed: 'Tonnes traitées',
    environmentalScore: 'Score environnemental',
    financialOverview: 'Vue financière',
    costBreakdown: 'Répartition des coûts',
    crossSiteComparison: 'Comparaison inter-chantiers',
    topSitesByTonnage: 'Top chantiers par tonnage',
    regulatoryRisk: 'Matrice de risques réglementaires',
    openNonConformities: 'Non-conformités ouvertes',
    upcomingDeadlines: 'Échéances à venir',
    environmentalImpact: 'Impact environnemental',
    co2Avoided: 'CO2 évité',
    landfillDiversion: 'Détournement décharge',
    circularEconomy: 'Économie circulaire',
    quickActions: 'Actions rapides',
    downloadReport: 'Télécharger rapport executive',
    viewCompliance: 'Voir la conformité',
    exportData: 'Exporter les données',
    tonnes: 'tonnes',
    tco2: 'tCO2',
    recyclage: 'Recyclage',
    elimination: 'Élimination',
    valorisation: 'Valorisation',
    noData: 'Aucune donnée disponible',
    vsLastMonth: 'vs mois précédent',
    months6: '6 mois',
    severity: {
      critical: 'Critique',
      major: 'Majeur',
      minor: 'Mineur',
    },
    countries: {
      BE: 'Belgique',
      FR: 'France',
      NL: 'Pays-Bas',
      DE: 'Allemagne',
    },
  },
  onboarding: {
    title: 'Bienvenue sur WasteSite',
    subtitle: 'Configurons votre espace en quelques minutes',
    step1Title: 'Votre entreprise',
    step2Title: 'Premier chantier',
    step3Title: 'Types de déchets',
    step4Title: 'Prêt !',
    companyName: 'Nom de l\'entreprise',
    vatNumber: 'Numéro TVA',
    country: 'Pays',
    address: 'Adresse',
    email: 'Email de contact',
    siteName: 'Nom du chantier',
    siteAddress: 'Adresse du chantier',
    workType: 'Type de travaux',
    workTypes: { construction: 'Construction', demolition: 'Démolition', renovation: 'Rénovation' },
    wasteTypesTitle: 'Déchets courants',
    wasteTypesDesc: 'Sélectionnez les types de déchets que vous gérez le plus souvent',
    readyTitle: 'Tout est prêt !',
    readyDesc: 'Votre espace WasteSite est configuré. Voici quelques astuces pour bien démarrer.',
    tip1: 'Scannez les codes-barres sur le terrain',
    tip2: 'Utilisez l\'assistant IA pour classifier vos déchets',
    tip3: 'L\'app fonctionne même hors connexion',
    goToDashboard: 'Aller au tableau de bord',
    next: 'Suivant',
    back: 'Retour',
    skip: 'Passer',
    skipWarning: 'Compléter la configuration aide à la conformité',
  },
  auditor: {
    title: 'Portail Auditeur',
    readOnly: 'Lecture seule',
    auditMode: 'Mode audit',
    accessDenied: 'Accès refusé',
    accessDeniedDesc: 'Le jeton d\'accès est invalide ou expiré.',
    organization: 'Organisation',
    expiresAt: 'Expire le',
    tabs: { sites: 'Chantiers', waste: 'Déchets', bordereaux: 'Bordereaux', compliance: 'Conformité', stats: 'Statistiques', chain: 'Traçabilité' },
    exportCsv: 'Exporter CSV',
    totalWaste: 'Tonnes totales',
    valorizationRate: 'Taux valorisation',
    bsdCompletionRate: 'Taux complétion BSD',
    openNonConformities: 'Non-conformités ouvertes',
    noData: 'Aucune donnée pour cette période',
    chain: {
      title: 'Chain-of-Custody',
      searchPlaceholder: 'Rechercher par N BSD ou code EWC...',
      producer: 'Producteur',
      collector: 'Collecteur',
      treatmentFacility: 'Centre de traitement',
      signed: 'Signé',
      pending: 'En cours',
      notYet: 'En attente',
      weightDeparture: 'Pesée départ',
      weightArrival: 'Pesée arrivée',
      weightDiscrepancy: 'Écart de pesée',
      discrepancyWarning: 'Écart supérieur à 5%',
      missingSignature: 'Signature manquante',
      noWeight: 'Non pesée',
      address: 'Adresse',
      driver: 'Chauffeur',
      plate: 'Plaque',
      signedAt: 'Signé le',
      signedBy: 'Signé par',
      noBsd: 'Aucun BSD trouvé',
      noBsdDesc: 'Modifiez vos critères de recherche.',
      filterEwc: 'Filtrer par code EWC',
    },
  },
  bsdInbox: {
    title: 'Inbox BSD',
    pending: 'En attente',
    accepted: 'Accepté',
    rejected: 'Rejeté',
    completed: 'Termine',
    all: 'Tous',
    filterByRole: 'Filtrer par rôle',
    producer: 'Producteur',
    collector: 'Collecteur',
    treatmentFacility: 'Installation de traitement',
    accept: 'Accepter',
    reject: 'Rejeter',
    confirmAccept: 'Accepter ce bordereau ? Cette action est irréversible.',
    confirmReject: 'Rejeter ce bordereau ? Veuillez indiquer un motif.',
    emptyState: 'Aucun élément dans votre inbox',
    emptyStatePending: 'Aucun bordereau en attente de réponse',
    sender: 'Expéditeur',
    wasteDescription: 'Description du déchet',
    ewcCode: 'Code EWC',
    quantity: 'Quantité',
    date: 'Date',
    status: 'Statut',
    role: 'Rôle',
    acceptSuccess: 'Bordereau accepté avec succès',
    rejectSuccess: 'Bordereau rejeté',
    loadMore: 'Charger plus',
    count: '{count} en attente',
    bordereauNumber: 'N\u00b0 BSD',
    rejectionReason: 'Motif de rejet',
    rejectionReasonPlaceholder: 'Indiquez la raison du rejet...',
  },
  landing: {
    contact: 'Contact',
    heroTitle1: 'Vos BSD construction,',
    heroTitle2: 'conformes le jour 1.',
    heroDesc: 'Suivi en temps réel des déchets de chantier, BSD numériques, registre légal DIWASS. PME BTP en BE, NL, LU, FR, DE, IT, ES.',
    requestAccess: 'Demander un accès',
    watchDemo: 'Voir la démo',
    features: [
      { title: 'Déclarations conformes', description: 'Générez vos BSD en 3 clics, conformes DIWASS et réglementation européenne.' },
      { title: 'Traçabilité complète', description: 'Suivez chaque déchet du chantier au centre de traitement. Chain-of-custody garantie.' },
      { title: 'Taux de valorisation', description: 'Tableaux de bord temps réel : tonnage, valorisation, coûts. Objectifs EU automatiques.' },
      { title: 'Conformité réglementaire', description: 'Échéances, registres légaux, rapports inspecteurs. Toujours prêt pour un contrôle.' },
    ],
    statsTitle: 'Déjà adopté par le secteur BTP',
    stats: [
      { value: '2 500+', label: 'Chantiers suivis' },
      { value: '70%', label: 'Taux valorisation moyen' },
      { value: '15 000+', label: 'BSD générés' },
      { value: '98%', label: 'Taux de conformité' },
    ],
    howItWorksTitle: 'Comment ça marche',
    howItWorksSubtitle: 'De la saisie terrain au rapport de conformité, en 3 étapes.',
    steps: [
      { title: '1. Saisissez sur le terrain', description: 'Scannez un code-barres, prenez une photo ou saisissez manuellement. L\'IA classifie automatiquement le déchet et trouve le code EWC.' },
      { title: '2. Générez vos documents', description: 'BSD, registre des déchets, bon de pesée : tout est généré automatiquement, signé électroniquement et archivé.' },
      { title: '3. Pilotez votre conformité', description: 'Tableau de bord en temps réel, alertes échéances, rapports PDF pour les inspecteurs. Objectif EU 70% suivi automatiquement.' },
    ],
    testimonialsTitle: 'Ce que disent nos utilisateurs',
    testimonials: [
      { quote: 'On a divisé par 3 le temps passé sur la paperasse déchets. L\'appli est simple et le support réactif.', author: 'Marc Dupont', role: 'Chef de chantier', company: 'Bouygues Construction' },
      { quote: 'Le scan OCR des bons de pesée nous fait gagner un temps fou. Plus d\'erreurs de saisie.', author: 'Sophie Laurent', role: 'Responsable environnement', company: 'Eiffage Benelux' },
      { quote: 'Enfin un outil qui comprend la réglementation belge ET française. Indispensable pour nos chantiers transfrontaliers.', author: 'Jean-Pierre Willems', role: 'Directeur technique', company: 'CIT Blaton' },
    ],
    pricingTitle: 'Tarifs simples et transparents',
    pricingSubtitle: 'Gratuit pour découvrir. Trials payants pour tester en conditions réelles.',
    plans: [
      {
        name: 'Starter',
        price: '0',
        period: '',
        description: 'Découvrez WasteSite',
        features: ['1 chantier actif', '5 déclarations/mois', '5 requêtes IA/jour', 'Calendrier conformité'],
        cta: 'Commencer gratuitement',
      },
      {
        name: 'Contractor',
        price: '49',
        period: '/mois',
        annualPrice: '490',
        annualPeriod: '/an',
        annualSaving: '2 mois offerts',
        description: 'Pour les PME du BTP',
        trial: '9 EUR pour 30 jours',
        features: ['3 utilisateurs inclus (+9 EUR/user)', '10 chantiers actifs', 'Déclarations illimitées', '100 scans OCR/mois', 'BSD automatiques', 'Rapports conformité', 'Export PDF/CSV', 'Multi-pays (BE/FR/NL/LU)', 'Support prioritaire'],
        cta: 'Essayer 30j pour 9 EUR',
        popular: true,
      },
      {
        name: 'Collector',
        price: '149',
        period: '/mois',
        annualPrice: '1490',
        annualPeriod: '/an',
        annualSaving: '2 mois offerts',
        description: 'Pour les collecteurs de déchets',
        trial: '49 EUR pour 30 jours',
        features: ['10 utilisateurs inclus (+15 EUR/user)', 'Chantiers illimités', 'OCR illimité', 'Tournées & véhicules', 'Inbox BSD cross-tenant', 'Annuaire collecteurs', 'Multi-pays complet', 'Support prioritaire'],
        cta: 'Essayer 30j pour 49 EUR',
      },
      {
        name: 'Enterprise',
        price: 'Sur devis',
        period: '',
        description: 'Pour les grands groupes BTP',
        features: ['Utilisateurs illimités', 'Tout Collector +', 'API REST', 'Account manager dédié', 'SLA 99.9%', 'SSO / SAML', 'Portail inspecteurs DIWASS', 'Formation sur site'],
        cta: 'Nous contacter',
      },
    ],
    ctaTitle: 'Préparez DIWASS dès aujourd\'hui',
    ctaDesc: 'Échéance DIWASS : 21 mai 2026. Démarrez maintenant, sans carte bancaire.',
    ctaBtn: 'Demander un accès',
    trustedBy: 'Ils nous font confiance',
    footer: 'Atelya — Belgique — Hébergé en Europe',
    footerLinks: {
      product: 'Produit',
      legal: 'Mentions légales',
      privacy: 'Politique de confidentialité',
      terms: 'CGV',
      support: 'Support',
    },
  },
  siteDetail: {
    overview: 'Aperçu',
    waste: 'Déchets',
    bordereaux: 'Bordereaux',
    compliance: 'Conformité',
    history: 'Historique',
    totalTonnage: 'Tonnage total',
    bsdCount: 'Bordereaux',
    valorizationRate: 'Taux valorisation',
    ncCount: 'Non-conformités',
    generalInfo: 'Informations générales',
    contactResponsible: 'Contact responsable',
    contact: 'Contact',
    phone: 'Téléphone',
    latitude: 'Latitude',
    longitude: 'Longitude',
    createdAt: 'Créé le',
    updatedAt: 'Modifié le',
    surfaceM2: 'Surface (m²)',
    wasteEntries: 'Déclarations de déchets',
    filterByDate: 'Filtrer par date',
    filterByEwc: 'Filtrer par code EWC',
    ewcCode: 'Code EWC',
    quantity: 'Quantité (kg)',
    destination: 'Destination',
    date: 'Date',
    totalByType: 'Totaux par type',
    allBsd: 'Tous les BSD',
    bsdNumber: 'N\u00b0 BSD',
    status: 'Statut',
    collector: 'Collecteur',
    nonConformities: 'Non-conformités',
    deadlines: 'Échéances',
    siteValorization: 'Taux valorisation du site',
    severity: 'Sévérité',
    type: 'Type',
    description: 'Description',
    ncStatus: 'Statut NC',
    openNc: 'NC ouvertes',
    timeline: 'Chronologie',
    noWaste: 'Aucune déclaration',
    noWasteDesc: 'Aucune déclaration de déchets pour ce chantier.',
    noBsd: 'Aucun bordereau',
    noBsdDesc: 'Aucun bordereau lié à ce chantier.',
    noNc: 'Aucune non-conformité',
    noNcDesc: 'Aucune non-conformité détectée pour ce chantier.',
    noHistory: 'Aucun événement',
    noHistoryDesc: 'L\'historique des actions apparaîtra ici.',
    eventTypes: {
      site_created: 'Chantier créé',
      waste_added: 'Déchet ajouté',
      bsd_created: 'BSD créé',
      bsd_signed: 'BSD signé',
      nc_opened: 'NC ouverte',
      nc_resolved: 'NC résolue',
    },
    map: 'Carte',
    estimation: {
      title: 'Estimation IA des déchets',
      subtitle: 'Quantités prévisionnelles par code EWC, avant travaux',
      generate: 'Générer une estimation IA',
      regenerate: 'Régénérer l\'estimation',
      generating: 'Génération en cours...',
      generatedByAi: 'Généré par IA',
      generatedByFallback: 'Généré par ratios sectoriels',
      totalKg: 'Total estimé',
      dangerousKg: 'Dont dangereux',
      dangerous: 'Dangereux',
      valorizationRate: 'Taux de valorisation estimé',
      dnshCompliant: 'Conforme DNSH (objectif 70%)',
      dnshNonCompliant: 'Sous l\'objectif DNSH (70%)',
      confidence: 'Confiance',
      noData: 'Aucune estimation encore générée',
      noDataDesc: 'Lancez une estimation IA pour obtenir une prévision des déchets attendus sur ce chantier.',
      error: 'Impossible de générer l\'estimation. Réessayez.',
      defaultsNotice: 'Type de projet, type de bâtiment ou surface non renseignés — estimation basée sur des valeurs par défaut.',
    },
  },
  vehicles: {
    title: 'Véhicules',
    newVehicle: 'Nouveau véhicule',
    plate: 'Plaque',
    type: 'Type',
    capacity: 'Capacité',
    unit: 'Unité',
    adr: 'ADR',
    adrCertified: 'Certifié ADR (matières dangereuses)',
    status: 'Statut',
    noVehicles: 'Aucun véhicule',
    noVehiclesDesc: 'Ajoutez vos véhicules de collecte pour les associer aux bordereaux.',
    plateLabel: "Plaque d'immatriculation",
    errorCreate: 'Erreur lors de la création du véhicule',
    types: {
      camion_benne: 'Camion benne',
      camion_grue: 'Camion grue',
      utilitaire: 'Utilitaire',
      semi: 'Semi-remorque',
    },
    statuses: {
      active: 'Actif',
      maintenance: 'Maintenance',
      retired: 'Retiré',
    },
  },
  rounds: {
    title: 'Tournées de collecte',
    newRound: 'Nouvelle tournée',
    date: 'Date',
    driver: 'Chauffeur (ID)',
    driverPlaceholder: 'ID du chauffeur',
    vehicle: 'Véhicule (ID)',
    vehiclePlaceholder: 'ID du véhicule',
    stops: 'Arrêts',
    stopPlaceholder: "Adresse de l'arrêt",
    addStop: 'Ajouter un arrêt',
    totalKg: 'Total (kg)',
    noRounds: 'Aucune tournée',
    noRoundsDesc: 'Créez votre première tournée de collecte.',
    errorLoading: 'Erreur de chargement des tournées',
    errorCreate: 'Erreur lors de la création',
    statuses: {
      planned: 'Planifiée',
      in_progress: 'En cours',
      completed: 'Terminée',
    },
  },
  driverDashboard: {
    myRound: 'Ma tournée',
    noRoundToday: 'Pas de tournée aujourd\'hui',
    noRoundTodayDesc: 'Aucune tournée planifiée ou en cours pour aujourd\'hui.',
    viewAllRounds: 'Voir toutes les tournées',
    vehicleAssigned: 'Véhicule assigné',
    notStarted: 'Non démarré',
    estimatedEnd: 'Fin est.',
    tonnesCollected: 'Tonnes',
    bsdProcessed: 'BSD traités',
    elapsed: 'Temps',
    roundCompleted: 'Tournée terminée',
    stopsTimeline: 'Arrêts',
    arrivedAt: 'Arrivé',
    departedAt: 'Parti',
    arrived: 'Arrivé',
    departed: 'Parti',
    completeRound: 'Terminer la tournée',
    completing: 'Clôture en cours...',
    remainingStops: '{count} arrêt(s) restant(s)',
  },
  demo: {
    title: 'Mode démo',
    expiration: 'Vos données expirent dans {days} jour(s)',
    upgrade: 'Passez en mode réel',
    closeAriaLabel: 'Fermer la bannière démo',
  },
  verify: {
    subtitle: 'Vérification de document réglementaire',
    loading: 'Vérification en cours...',
    verified: 'Document vérifié',
    reportType: 'Type de rapport',
    organization: 'Organisation',
    period: 'Période',
    generatedAt: 'Date de génération',
    notFound: 'Document non trouvé',
    notFoundDesc: 'Ce code de vérification ne correspond à aucun document enregistré.',
    errorTitle: 'Erreur de vérification',
    errorGeneric: 'Une erreur est survenue lors de la vérification.',
  },
  team: {
    title: 'Équipe',
    subtitle: 'Gérez les collaborateurs de votre organisation',
    invite: 'Inviter un collaborateur',
    inviteTitle: 'Inviter un collaborateur',
    inviteSubtitle: 'Envoyez une invitation par email pour rejoindre votre organisation',
    email: 'Email',
    firstName: 'Prénom',
    lastName: 'Nom',
    role: 'Rôle',
    status: 'Statut',
    roles: {
      admin: 'Administrateur',
      adminDesc: 'Accès complet : facturation, équipe, paramètres',
      office_manager: 'Office Manager',
      office_managerDesc: 'Gestion administrative : chantiers, déchets, BSD, rapports, conformité',
      manager: 'Chef de chantier',
      managerDesc: 'Gestion terrain : chantiers, déchets, BSD, rapports',
      operator: 'Opérateur terrain',
      operatorDesc: 'Saisie de déchets, bordereaux, scanner',
      driver: 'Chauffeur',
      driverDesc: 'Tournées, bordereaux, scanner, géolocalisation',
    },
    statuses: {
      invited: 'Invité',
      active: 'Actif',
      suspended: 'Suspendu',
    },
    actions: {
      edit: 'Modifier le rôle',
      suspend: 'Suspendre',
      resend: 'Renvoyer l\'invitation',
      remove: 'Retirer',
    },
    empty: 'Aucun membre dans votre équipe. Invitez votre premier collaborateur.',
    inviteSuccess: 'Invitation envoyée avec succès',
    updateSuccess: 'Membre mis à jour',
    removeSuccess: 'Membre suspendu',
    stats: {
      total: 'Total membres',
      active: 'Actifs',
      pending: 'Invitations en attente',
    },
    confirmSuspend: 'Confirmer la suspension',
    confirmRemove: 'Confirmer le retrait',
  },
  contact: {
    title: 'Nous contacter',
    nameLabel: 'Nom',
    emailLabel: 'Email',
    companyLabel: 'Entreprise',
    phoneLabel: 'Téléphone',
    messagePlaceholder: 'Décrivez votre besoin ou posez votre question...',
    submit: 'Envoyer',
    sending: 'Envoi en cours...',
    successTitle: 'Message envoyé !',
    successMessage: 'Nous vous contacterons dans les 24h.',
    errorMessage: 'Une erreur est survenue. Veuillez réessayer.',
    required: 'Obligatoire',
  },
  glossary: {
    title: 'Glossaire',
    subtitle: 'Termes métier de la gestion des déchets de chantier',
    searchPlaceholder: 'Rechercher un terme...',
    exampleLabel: 'Exemple',
    noResults: 'Aucun terme ne correspond à votre recherche',
    ewc: {
      term: 'Code EWC',
      definition: 'Code à 6 chiffres du Catalogue Européen des Déchets qui identifie chaque type de déchet. Obligatoire sur les bordereaux de suivi.',
      example: '17 01 01 = Béton, 17 06 01 = Matériaux d\'isolation contenant de l\'amiante',
    },
    bsd: {
      term: 'BSD (Bordereau de Suivi des Déchets)',
      definition: 'Document réglementaire qui accompagne chaque transport de déchet du producteur au centre de traitement. Il garantit la traçabilité complète de la chaîne.',
      example: 'Un BSD est créé quand une benne de gravats quitte le chantier vers le centre de tri.',
    },
    diwass: {
      term: 'DIWASS',
      definition: 'Digital Waste Tracking System — système de traçabilité numérique des déchets imposé par la réglementation européenne. Remplace progressivement les BSD papier.',
      example: 'À partir du 21 mai 2026, le registre DIWASS est obligatoire pour tous les chantiers en Belgique.',
    },
    valorisation70: {
      term: 'Objectif de valorisation 70%',
      definition: 'Objectif fixé par la Directive-cadre 2008/98/CE : au moins 70% en poids des déchets de construction et démolition non dangereux doivent être valorisés (recyclage, réutilisation).',
      example: 'Un chantier qui envoie 80t en recyclage sur 100t totales atteint 80%, conforme à l\'objectif.',
    },
    repBatiment: {
      term: 'REP Bâtiment',
      definition: 'Responsabilité Élargie du Producteur appliquée au secteur du bâtiment. Les fabricants de matériaux financent la collecte et le recyclage des déchets de chantier.',
      example: 'En France, les éco-organismes comme Ecominero ou Valdelia gèrent la REP Bâtiment depuis 2023.',
    },
    adr: {
      term: 'ADR',
      definition: 'Accord européen relatif au transport international de marchandises Dangereuses par Route. Définit les règles d\'emballage, étiquetage et transport des déchets dangereux.',
      example: 'Le transport d\'amiante (UN 2590) nécessite un véhicule certifié ADR et un chauffeur formé.',
    },
    chainOfCustody: {
      term: 'Chaîne de traçabilité (Chain of Custody)',
      definition: 'Enchaînement documenté de toutes les étapes d\'un déchet, du producteur au traitement final. Chaque transfert est signé et pesé pour garantir la conformité.',
      example: 'Producteur -> Collecteur (signature + pesée départ) -> Centre de tri (pesée arrivée + traitement).',
    },
    nonConformity: {
      term: 'Non-conformité',
      definition: 'Écart par rapport à la réglementation ou aux procédures internes : BSD manquant, écart de pesée >5%, agrément expiré, code EWC incorrect.',
      example: 'Un écart de pesée de 8% entre le départ et l\'arrivée génère automatiquement une non-conformité.',
    },
    brudalex: {
      term: 'Brudalex',
      definition: 'Réglementation déchets de la Région de Bruxelles-Capitale (ordonnance 2012, arrêté 2016). Plus stricte que le régime wallon : traçabilité 100%, objectif valorisation 90%, plan de gestion obligatoire > 500 m², inventaire amiante pour bâtiments avant 2001.',
      example: 'Un chantier à Ixelles (1050) est soumis au Brudalex. Un BSD est requis pour chaque déchet, même 10 kg de gravats.',
    },
  },
  nonConformities: {
    statusOpen: 'Ouverte',
    statusInProgress: 'En cours',
    statusResolved: 'Résolue',
    statusEscalated: 'Escaladée',
    filterStatusAll: 'Tous les statuts',
    filterStatusOpen: 'Ouvertes',
    filterStatusInProgress: 'En cours',
    filterStatusResolved: 'Résolues',
    filterStatusEscalated: 'Escaladées',
    filterSeverityAll: 'Toutes sévérités',
    filterSeverityCritical: 'Critique',
    filterSeverityMajor: 'Majeure',
    filterSeverityMinor: 'Mineure',
    typeMissingBsd: 'BSD manquant',
    typeWeightDiscrepancy: 'Écart de pesée',
    typeExpiredAgreement: 'Accord expiré',
    typeWrongEwc: 'Code EWC incorrect',
    typeUnauthorizedWaste: 'Déchet non autorisé',
    typeOther: 'Autre',
    title: 'Non-conformités',
    searchPlaceholder: 'Non-conformités',
    colSeverity: 'Sévérité',
    colType: 'Type',
    colDescription: 'Description',
    colStatus: 'Statut',
    colDate: 'Date',
    none: 'Aucune non-conformité',
    noneDesc: 'Aucune non-conformité enregistrée.',
    noneFiltered: 'Aucune non-conformité ne correspond aux filtres sélectionnés.',
    count: '{n} non-conformité',
    countPlural: '{n} non-conformités',
    filterActive: '(filtre actif)',
    errorLoading: 'Erreur de chargement',
    errorResolving: 'Erreur de résolution',
    modalTitle: 'Détail non-conformité',
    fieldType: 'Type',
    fieldDescription: 'Description',
    fieldDetectedAt: 'Date de détection',
    fieldResolution: 'Résolution',
    fieldResolvedAt: 'Résolue le',
    resolutionLabel: 'Résolution',
    resolutionPlaceholder: 'Décrivez les actions correctives prises...',
    markResolved: 'Marquer résolu',
    breadcrumb: 'Non-conformités',
    sectionInfo: 'Informations',
    sectionDescription: 'Description',
    sectionRelated: 'Éléments liés',
    relatedBsd: 'BSD lié',
    relatedSite: 'Chantier lié',
    sectionWorkflow: 'Workflow',
    sectionResolution: 'Résolution',
    resolvedOn: 'Résolue le',
    sectionActions: 'Actions',
    changeStatus: 'Changer le statut :',
    selectStatus: '-- Sélectionner --',
    toInProgress: 'En cours',
    toEscalated: 'Escalader',
    resumeInProgress: 'Reprendre en cours',
    apply: 'Appliquer',
    markAsResolved: 'Marquer comme résolue',
    createdOn: 'Créé le',
    updatedOn: 'Modifié le',
  },
  actionTiles: {
    title: 'Actions rapides',
    subtitle: 'Tout ce que vous pouvez faire sur le terrain.',
    empty: 'Aucune action disponible pour votre rôle.',
    pendingAria: 'en attente',
    scan: 'Scanner BSD',
    signBsd: 'Signer BSD',
    weight: 'Nouvelle pesée',
    rounds: 'Mes tournées',
    wasteEntry: 'Nouvelle entrée',
    photo: 'Photo + géoloc',
    newSite: 'Nouveau chantier',
    nc: 'Non-conformités',
  },
}
